import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { OrganizationRoles, TransactionFilterOptions } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useEventListener } from 'src/hooks'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectCreditCardsUserOrViewerMeCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

interface ExpensesTransactionsProps {
  myTransactions?: boolean
}

const ExpensesTransactions: React.FC<ExpensesTransactionsProps> = (props) => {
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const { results: statistics, anyLoading: reactQueryStatisticsLoading } =
    useQueryStatistics({
      statisticsKeys: [
        StatisticsKeys.CreditCardsSourcePhysicalClearingsLinkedCount,
      ],
    })
  const creditCardsSourcePhysicalClearingsLinkedCount =
    statistics?.CreditCardsSourcePhysicalClearingsLinkedCount?.data ?? 0

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)

  const creditCardsUserOrViewerMeCount = useSelector(
    selectCreditCardsUserOrViewerMeCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest

  const [initialLoad, setInitialLoad] = useState(true)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })
  const clientHeight = useMemo(
    () => viewportHeight.height - 122,
    [viewportHeight],
  )

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEventListener('resize', updateWindowSize)

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
    ])
  }, [dispatchStatistics])

  return !statisticsLoaded.creditCardsUserOrViewerMeCountStatisticsLoaded ||
    reactQueryStatisticsLoading ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Transactions | ${appName}`}</title>
        <meta name="description" content={`Transactions page of ${appName}`} />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ReportTransactionsBoxAndLinks
          context={
            props.myTransactions
              ? TransactionReportType.MyTransactions
              : TransactionReportType.Expenses
          }
          showIntegrationsLink={true}
          showConnection
          design={TransactionsTableLargeDesigns.UserAndCard}
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          canViewAllTransactions={!(creditCardsUserOrViewerMeCount <= 0)}
          visibleFilters={[
            TransactionFilterOptions.Date,
            ...[TransactionFilterOptions.TransactionAmountRange],
            TransactionFilterOptions.TransactionStatus,
            // using creditCardsSourcePhysicalClearingsLinkedCount > 0 check until BE updates Regions cards to accurately set physicalClearingsEnabled
            ...(organization?.physicalClearingsEnabled ||
            creditCardsSourcePhysicalClearingsLinkedCount > 0
              ? [TransactionFilterOptions.TransactionOrigin]
              : []),
            TransactionFilterOptions.ExpenseCategoryStatus,
            TransactionFilterOptions.ExpenseCategoryLabels,
            ...(receiptManagementEnabled !== undefined
              ? [TransactionFilterOptions.ReceiptStatus]
              : []),
            ...(activeIntegration !== undefined &&
            user?.hasOrgBookkeeperPermissions
              ? [
                  TransactionFilterOptions.TransactionSyncStatus,
                  TransactionFilterOptions.ReceiptSyncStatus,
                ]
              : []),
            ...(!isGuestUser ? [TransactionFilterOptions.CardUser] : []),
            TransactionFilterOptions.Departments,
            ...(!isGuestUser ? [TransactionFilterOptions.Account] : []),
            TransactionFilterOptions.MerchantCategory,
            TransactionFilterOptions.Search,
          ]}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </>
  )
}

export default ExpensesTransactions
